import React, { useState, useEffect, memo, useCallback } from "react";
import {
  Box,
  Grid,
  TextField,
  Autocomplete,
  CircularProgress,
  IconButton,
  Stack,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { useSelector } from "react-redux";
import configServ from "../../services/config";
import toast from "react-hot-toast";

const initialForm = {
  customer_id: null,
  employee_code: "",
  designation_id: "",
  department_id: "",
  status: true,
};

// Read-only preview fields populated from the selected customer
const initialPreview = {
  contact_name: "",
  mobile: "",
  phone: "",
  email: "",
  address: "",
  type: "",
};

function EmployeeCard({
  isEdit,
  editData,
  setIsCreate,
  setIsChanged,
  clear,
  designationList,
  departmentList,
}) {
  const { company_id, user_id } = useSelector((state) => state.GlobalVariables);

  const [form, setForm] = useState(initialForm);
  const [preview, setPreview] = useState(initialPreview);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Autocomplete state
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Populate form when editing
  useEffect(() => {
    if (isEdit && editData?.id) {
      setForm({
        customer_id: editData.customer_id,
        employee_code: editData.employee_code || "",
        designation_id: editData.designation_id || "",
        department_id: editData.department_id || "",
        status: Boolean(editData.status),
      });
      setPreview({
        contact_name: editData.contact_name || "",
        mobile: editData.mobile || "",
        phone: editData.phone || "",
        email: editData.email || "",
        address: editData.address || "",
        type: editData.type || "",
      });
      // Synthesise a customer option so the autocomplete shows the name
      setSelectedCustomer({ id: editData.customer_id, contact_name: editData.contact_name });
      setInputValue(editData.contact_name || "");
    }
  }, [editData, isEdit]);

  // Debounced search
  useEffect(() => {
    if (isEdit) return;           // no autocomplete search needed in edit mode
    if (inputValue.length < 2) {
      setOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await configServ.searchCustomers({ company_id, search: inputValue });
        setOptions(res?.status ? res.data : []);
      } catch {
        setOptions([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [inputValue, company_id, isEdit]);

  const handleCustomerSelect = (_, customer) => {
    setSelectedCustomer(customer);
    if (customer) {
      setForm((prev) => ({ ...prev, customer_id: customer.id }));
      setPreview({
        contact_name: customer.contact_name || "",
        mobile: customer.mobile || "",
        phone: customer.phone || "",
        email: customer.email || "",
        address: customer.address || "",
        type: customer.type || "",
      });
      if (errors.customer_id) setErrors((prev) => ({ ...prev, customer_id: "" }));
    } else {
      setForm((prev) => ({ ...prev, customer_id: null }));
      setPreview(initialPreview);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleReset = useCallback(() => {
    setForm(initialForm);
    setPreview(initialPreview);
    setErrors({});
    setSelectedCustomer(null);
    setInputValue("");
    setOptions([]);
    clear();
  }, [clear]);

  const validate = () => {
    const err = {};
    if (!form.customer_id) err.customer_id = "Please select a customer";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      let res;
      if (isEdit) {
        res = await configServ.updateEmployee(editData.id, {
          ...form,
          company_id,
          updated_by: user_id,
        });
      } else {
        res = await configServ.createEmployee({
          ...form,
          company_id,
          created_by: user_id,
        });
      }

      if (res?.status) {
        toast.success(isEdit ? "Employee updated" : "Employee created");
        handleReset();
        setIsCreate(false);
        setIsChanged((p) => !p);
      } else {
        toast.error(res?.message || "Operation failed");
      }
    } catch (e) {
      toast.error("Something went wrong");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const labelSx = { backgroundColor: "white" };

  return (
    <Box sx={{ backgroundColor: "#e3f3fc", p: 2, borderRadius: 2, m: 1 }}>
      <Grid container spacing={2}>

        {/* Customer Autocomplete — hidden in edit mode (customer is fixed) */}
        {!isEdit && (
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              options={options}
              getOptionLabel={(o) =>
                o.contact_name || `${o.fname || ""} ${o.lname || ""}`.trim() || o.mobile || ""
              }
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              value={selectedCustomer}
              inputValue={inputValue}
              onInputChange={(_, val) => setInputValue(val)}
              onChange={handleCustomerSelect}
              loading={searching}
              noOptionsText={inputValue.length < 2 ? "Type to search…" : "No customers found"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Customer *"
                  size="small"
                  error={!!errors.customer_id}
                  helperText={errors.customer_id || "Search by name or mobile"}
                  sx={labelSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searching && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        )}

        {/* Preview fields — read-only, filled from selected customer */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="Name"
            value={preview.contact_name}
            fullWidth size="small"
            InputProps={{ readOnly: true }}
            sx={labelSx}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="Mobile"
            value={preview.mobile}
            fullWidth size="small"
            InputProps={{ readOnly: true }}
            sx={labelSx}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="Email"
            value={preview.email}
            fullWidth size="small"
            InputProps={{ readOnly: true }}
            sx={labelSx}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="Type"
            value={preview.type}
            fullWidth size="small"
            InputProps={{ readOnly: true }}
            sx={labelSx}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="Address"
            value={preview.address}
            fullWidth size="small"
            InputProps={{ readOnly: true }}
            sx={labelSx}
          />
        </Grid>

        {/* Editable HR fields */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Employee Code"
            name="employee_code"
            value={form.employee_code}
            onChange={handleChange}
            fullWidth size="small"
            sx={labelSx}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Designation</InputLabel>
            <Select
              name="designation_id"
              value={form.designation_id}
              label="Designation"
              onChange={handleChange}
              sx={labelSx}

            >
              {designationList?.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Department</InputLabel>
            <Select
              name="department_id"
              value={form.department_id}
              label="Department"
              onChange={handleChange}
              sx={labelSx}

            >
              {departmentList?.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>


        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex", alignItems: "center" }}>
          <FormControlLabel
            control={
              <Checkbox
                name="status"
                checked={Boolean(form.status)}
                onChange={handleChange}
              />
            }
            label="Active"
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12} textAlign="right">
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <IconButton
              onClick={handleReset}
              sx={{ backgroundColor: "secondary.main", color: "white", "&:hover": { backgroundColor: "secondary.dark" } }}
              title="Reset"
            >
              <RotateLeftIcon />
            </IconButton>
            <IconButton
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                backgroundColor: isEdit ? "success.main" : "primary.main",
                color: "white",
                "&:hover": { backgroundColor: isEdit ? "success.dark" : "primary.dark" },
              }}
              title={isEdit ? "Update" : "Save"}
            >
              <SaveIcon />
            </IconButton>
          </Stack>
        </Grid>

      </Grid>
    </Box>
  );
}

export default memo(EmployeeCard);
